import { useT } from "../../i18n/LanguageProvider";
import { categoryLabel } from "../../i18n/taxonomy";
import { bouquetFloorPill, type BouquetFloor } from "./kioskCostDisplay";
import { eur, uomLabel } from "./shopFloorFormat";
import { cartLineCents, type TicketCart } from "./ticketCart";

/**
 * A floor ticket's lines (G-147: shared by the till and "Customer collected"'s
 * "+ Add items") — quantity steppers capped at stock, the price each line is
 * charged at, the suggested price beside it, and the line total.
 */
export function ShopFloorTicketLines({ ticket }: { ticket: TicketCart }) {
  const { t } = useT();
  return (
    <>
      {ticket.cart.map((line) => (
        <div key={line.uid} className="shop-floor-sale-ticket-line">
          <div className="shop-floor-sale-ticket-line-head">
            <div>
              <strong>{line.label}</strong>
              <span>{line.kind === "BOUQUET"
                  ? t("sale.bouquet")
                  : categoryLabel(line.categoryCode, line.categoryLabel)}</span>
            </div>
            <button
              type="button"
              className="shop-floor-sale-remove-line"
              onClick={() => ticket.removeLine(line.uid)}
              aria-label={t("sale.removeFromTicket", { label: line.label })}
            >
              ×
            </button>
          </div>

          {line.kind === "ITEM" ? (
            <div className="shop-floor-sale-line-controls">
              <span className="shop-floor-sale-quantity-control">
                <button
                  type="button"
                  onClick={() => ticket.changeItemQuantity(line, -1)}
                  aria-label={
                    Number(line.qtyStr) <= 1
                      ? t("sale.removeFromTicket", { label: line.label })
                      : t("sale.decrease", { label: line.label })
                  }
                >
                  −
                </button>
                <input
                  type="number"
                  min={0}
                  max={line.availableQty}
                  step={line.uom === "UNIT" || line.uom === "ROLL" ? 1 : 0.001}
                  value={line.qtyStr}
                  onChange={(event) => ticket.updateLine(line.uid, { qtyStr: event.target.value })}
                  onBlur={() => ticket.commitItemQuantity(line)}
                  aria-label={t("sale.quantityAria", { label: line.label })}
                />
                <button
                  type="button"
                  onClick={() => ticket.changeItemQuantity(line, 1)}
                  disabled={Number(line.qtyStr) >= line.availableQty}
                  aria-label={t("sale.increase", { label: line.label })}
                >
                  +
                </button>
              </span>
              <span className="shop-floor-sale-line-stock">
                {t("sale.ofAvailable", {
                  qty: line.availableQty,
                  unit: uomLabel(line.uom, line.availableQty),
                })}
              </span>
            </div>
          ) : null}

          <div className="shop-floor-sale-price-row">
            <label htmlFor={`sale-line-price-${line.uid}`}>
              {line.kind === "ITEM" ? t("sale.priceEach") : t("sale.salePrice")}
            </label>
            <span>
              €
              <input
                id={`sale-line-price-${line.uid}`}
                type="number"
                min={0}
                step={0.01}
                inputMode="decimal"
                value={line.priceStr}
                onChange={(event) => ticket.updateLine(line.uid, { priceStr: event.target.value })}
                aria-label={t("sale.salePriceAria", { label: line.label })}
              />
            </span>
          </div>
          {line.suggestedStr != null ? (
            <p className="shop-floor-sale-suggested">
              {t("sale.suggestedPrice", { price: eur(Number(line.suggestedStr)) })}
            </p>
          ) : null}
          {line.kind === "BOUQUET" ? <FloorPill floor={line.floor} /> : null}
          <div className="shop-floor-sale-line-total">
            <span>{t("sale.lineTotal")}</span>
            <strong>
              {eur(cartLineCents(line) / 100)}
            </strong>
          </div>
        </div>
      ))}
    </>
  );
}

/**
 * WO-B (OD-198): where a bouquet's floor would be, "Floor: cannot check" when
 * part of its cost is unknown — the till never warns "below the floor" for it.
 */
function FloorPill({ floor }: { floor: BouquetFloor }) {
  const pill = bouquetFloorPill(floor);
  return pill ? (
    <p className="shop-floor-sale-floor-unknown">
      <span className="badge warning">{pill}</span>
    </p>
  ) : null;
}
